'use client';

/**
 * Clearinghouse Settings Component
 *
 * Allows organizations to configure their claims clearinghouse settings:
 * - Select clearinghouse (Availity, Change Healthcare, etc.)
 * - Configure submission credentials
 * - Set production vs test mode
 * - View connection status
 * - Track last submission
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Building2,
  Check,
  X,
  Edit,
  Save,
  AlertCircle,
  CheckCircle,
  Globe,
  Lock,
  RefreshCw,
  Plus
} from 'lucide-react';

interface ClearinghouseConfig {
  id?: number;
  organization_id: number;
  clearinghouse: string;
  submitter_id?: string;
  receiver_id?: string;
  submission_method: string;
  is_production: boolean;
  is_active: boolean;
  last_submission_date?: string;
  connection_status?: 'connected' | 'disconnected' | 'error';
  total_submissions?: number;
}

interface ClearinghouseSettingsProps {
  organizationId: number;
  organizationName?: string;
}

// Supported clearinghouses for claims submission
// Note: 'stedi' is the platform-default clearinghouse — keys are configured
// server-side via STEDI_API_KEY env var on Render, not per-org.
const CLEARINGHOUSES = [
  { value: 'stedi', label: 'Stedi', description: 'Platform default (Healthcare Claims API) — credentials managed server-side' },
  { value: 'availity', label: 'Availity', description: 'Nationwide clearinghouse' },
  { value: 'change_healthcare', label: 'Change Healthcare', description: 'Formerly Emdeon' },
  { value: 'eprovider_solutions', label: 'eProvider Solutions', description: 'California Medi-Cal clearinghouse' },
  { value: 'waystar', label: 'Waystar', description: 'Formerly ZirMed' },
  { value: 'office_ally', label: 'Office Ally', description: 'Free clearinghouse option' },
  { value: 'trizetto', label: 'TriZetto', description: 'Gateway EDI' },
  { value: 'relay_health', label: 'Relay Health', description: 'McKesson clearinghouse' },
  { value: 'collaboratemd', label: 'CollaborateMD', description: 'Integrated PM/billing clearinghouse' },
  { value: 'other', label: 'Other', description: 'Custom clearinghouse' }
];

const SUBMISSION_METHODS = [
  { value: 'api', label: 'API', description: 'Real-time API submission' },
  { value: 'sftp', label: 'SFTP', description: 'Secure file transfer' },
  { value: 'portal', label: 'Portal', description: 'Manual portal upload' }
];

export function ClearinghouseSettings({
  organizationId,
  organizationName
}: ClearinghouseSettingsProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [config, setConfig] = useState<ClearinghouseConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);

  const [formData, setFormData] = useState<ClearinghouseConfig>({
    organization_id: organizationId,
    clearinghouse: 'availity',
    submission_method: 'api',
    is_production: false,
    is_active: true
  });

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId })
    };
  };

  useEffect(() => {
    fetchConfig();
  }, [organizationId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/cbos/${organizationId}/clearinghouse-settings`),
        { credentials: 'include', headers: getAuthHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setFormData(data);
      } else if (response.status === 404) {
        // No config exists yet
        setConfig(null);
        setEditing(true);
      }
    } catch (err: any) {
      console.error('Error fetching clearinghouse config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const url = config?.id
        ? `/api/cbos/${organizationId}/clearinghouse-settings/${config.id}`
        : `/api/cbos/${organizationId}/clearinghouse-settings`;

      const method = config?.id ? 'PUT' : 'POST';

      const response = await fetch(getWeaveinApiUrl(url), {
        method,
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setEditing(false);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save settings');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setError(null);

    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/cbos/${organizationId}/clearinghouse-settings/test-connection`),
        {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(data.success ? 'Connection successful!' : 'Connection failed');
        await fetchConfig();
      } else {
        throw new Error('Failed to test connection');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTestingConnection(false);
    }
  };

  const getConnectionStatusBadge = () => {
    if (!config) return null;

    const status = config.connection_status || 'disconnected';
    const badges = {
      connected: <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
        <CheckCircle className="h-3 w-3" />
        {t('billing.connected', 'Connected')}
      </Badge>,
      disconnected: <Badge className="bg-gray-100 text-gray-800 flex items-center gap-1">
        <X className="h-3 w-3" />
        {t('billing.not_connected', 'Not Connected')}
      </Badge>,
      error: <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        {t('billing.error', 'Error')}
      </Badge>
    };

    return badges[status];
  };

  const selectedClearinghouse = CLEARINGHOUSES.find(ch => ch.value === formData.clearinghouse);
  const selectedMethod = SUBMISSION_METHODS.find(m => m.value === formData.submission_method);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            {t('billing.clearinghouse_settings', 'Clearinghouse Settings')}
          </h2>
          {organizationName && (
            <p className="text-sm text-gray-600 mt-1">
              Configure claims submission for {organizationName}
            </p>
          )}
        </div>
        {config && !editing && (
          <Button onClick={() => setEditing(true)}>
            <Edit className="h-4 w-4 mr-2" />
            {t('billing.edit_settings', 'Edit Settings')}
          </Button>
        )}
      </div>

      {error && (
        <Card className="p-4 bg-red-50 border-red-200 text-red-800 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </Card>
      )}

      {loading && !config ? (
        <Card className="p-8 text-center text-gray-500">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          {t('billing.loading_settings', 'Loading settings...')}
        </Card>
      ) : editing ? (
        /* Edit Mode */
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">{t('billing.clearinghouse_configuration', 'Clearinghouse Configuration')}</h3>

          <div className="space-y-4">
            {/* Clearinghouse Selection */}
            <div>
              <Label htmlFor="clearinghouse">{t('billing.clearinghouse', 'Clearinghouse *')}</Label>
              <select
                id="clearinghouse"
                className="w-full p-2 border rounded"
                value={formData.clearinghouse}
                onChange={(e) => setFormData({ ...formData, clearinghouse: e.target.value })}
              >
                {CLEARINGHOUSES.map(ch => (
                  <option key={ch.value} value={ch.value}>
                    {ch.label} - {ch.description}
                  </option>
                ))}
              </select>
              {formData.clearinghouse === 'stedi' && (
                <div className="mt-2 p-3 rounded border bg-blue-50 border-blue-200 text-blue-900 text-sm">
                  <strong>Stedi (platform default):</strong> API key is configured server-side
                  via <code>STEDI_API_KEY</code> on Render — no per-org credentials needed here.
                  Set <strong>Submission Method = API</strong> and choose <strong>Production</strong> /
                  <strong> Test</strong> below. Submissions route through the Stedi Healthcare Claims
                  endpoint; webhooks return 277CA + 835 callbacks to{' '}
                  <code>/api/stedi/webhooks/event</code>.
                </div>
              )}
            </div>

            {/* Submission Method */}
            <div>
              <Label htmlFor="submission_method">{t('billing.submission_method', 'Submission Method *')}</Label>
              <select
                id="submission_method"
                className="w-full p-2 border rounded"
                value={formData.submission_method}
                onChange={(e) => setFormData({ ...formData, submission_method: e.target.value })}
              >
                {SUBMISSION_METHODS.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label} - {method.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Submitter ID */}
              <div>
                <Label htmlFor="submitter_id">{t('billing.submitter_id', 'Submitter ID')}</Label>
                <Input
                  id="submitter_id"
                  value={formData.submitter_id || ''}
                  onChange={(e) => setFormData({ ...formData, submitter_id: e.target.value })}
                  placeholder={t('billing.your_submitter_id', 'Your submitter ID')}
                />
              </div>

              {/* Receiver ID */}
              <div>
                <Label htmlFor="receiver_id">{t('billing.receiver_id', 'Receiver ID')}</Label>
                <Input
                  id="receiver_id"
                  value={formData.receiver_id || ''}
                  onChange={(e) => setFormData({ ...formData, receiver_id: e.target.value })}
                  placeholder={t('billing.clearinghouse_receiver_id', 'Clearinghouse receiver ID')}
                />
              </div>
            </div>

            {/* Environment Toggle */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded border">
              <Lock className="h-5 w-5 text-gray-500" />
              <div className="flex-1">
                <Label className="font-semibold">{t('billing.environment', 'Environment')}</Label>
                <p className="text-sm text-gray-600">
                  {formData.is_production
                    ? 'Production mode - Claims will be submitted for real processing'
                    : 'Test mode - Claims will be submitted to test environment'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="is_production" className="cursor-pointer text-sm">
                  {t('billing.production', 'Production')}
                </Label>
                <input
                  type="checkbox"
                  id="is_production"
                  checked={formData.is_production}
                  onChange={(e) => setFormData({ ...formData, is_production: e.target.checked })}
                  className="h-4 w-4"
                />
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Active (enable claim submissions)
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            {config && (
              <Button variant="outline" onClick={() => {
                setFormData(config);
                setEditing(false);
              }}>
                {t('billing.cancel', 'Cancel')}
              </Button>
            )}
            <Button onClick={handleSave} disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </Card>
      ) : config ? (
        /* View Mode */
        <div className="space-y-4">
          {/* Status Card */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">{t('billing.connection_status', 'Connection Status')}</h3>
              <div className="flex items-center gap-3">
                {getConnectionStatusBadge()}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestConnection}
                  disabled={testingConnection}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${testingConnection ? 'animate-spin' : ''}`} />
                  {t('billing.test_connection', 'Test Connection')}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500">{t('billing.clearinghouse_1', 'Clearinghouse')}</p>
                <p className="text-lg font-semibold mt-1">
                  {selectedClearinghouse?.label || config.clearinghouse}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('billing.submission_method_1', 'Submission Method')}</p>
                <p className="text-lg font-semibold mt-1">
                  {selectedMethod?.label || config.submission_method}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('billing.environment_1', 'Environment')}</p>
                <Badge className={config.is_production ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}>
                  {config.is_production ? 'Production' : 'Test'}
                </Badge>
              </div>
            </div>

            {(config.submitter_id || config.receiver_id) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t">
                {config.submitter_id && (
                  <div>
                    <p className="text-sm text-gray-500">{t('billing.submitter_id_1', 'Submitter ID')}</p>
                    <p className="text-lg font-mono mt-1">{config.submitter_id}</p>
                  </div>
                )}
                {config.receiver_id && (
                  <div>
                    <p className="text-sm text-gray-500">{t('billing.receiver_id_1', 'Receiver ID')}</p>
                    <p className="text-lg font-mono mt-1">{config.receiver_id}</p>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Statistics Card */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">{t('billing.submission_statistics', 'Submission Statistics')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500">{t('billing.total_submissions', 'Total Submissions')}</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {config.total_submissions || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('billing.last_submission', 'Last Submission')}</p>
                <p className="text-lg font-semibold mt-1">
                  {config.last_submission_date
                    ? fmt.short(config.last_submission_date)
                    : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('billing.status', 'Status')}</p>
                <Badge className={config.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                  {config.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </Card>
        </div>
      ) : (
        /* No Config */
        <Card className="p-12 text-center">
          <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">{t('billing.no_clearinghouse_configured', 'No Clearinghouse Configured')}</h3>
          <p className="text-gray-600 mb-6">
            {t('billing.configure_your_claims_clearinghouse_to', 'Configure your claims clearinghouse to enable electronic claim submissions.')}
          </p>
          <Button onClick={() => setEditing(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('billing.configure_clearinghouse', 'Configure Clearinghouse')}
          </Button>
        </Card>
      )}
    </div>
  );
}

export default ClearinghouseSettings;
